import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Activity, ChevronRight, Loader2, CheckCircle2, XCircle,
  Layers, Clock,
} from "lucide-react";

/**
 * Top-bar activity pill (Phase 75).
 *
 * Polls ``GET /api/activity`` every 5 s so the user can see, from any
 * page in the app, how many analyzer sessions are running / queued /
 * recently finished across every one of their jobs. Clicking the pill
 * opens a right-side slide-in list; each session card is clickable
 * and deep-links back to its job page.
 *
 * The endpoint hides sibling batches (Phase 67) so the counts here
 * match what the user sees on each job's Recent Analyses card — one
 * row per logical folder analysis, not one per raw batch.
 */
export default function ActivityPill() {
  const nav = useNavigate();
  const [activity, setActivity] = useState({
    running: [], queued: [], recent: [],
    counts: { running: 0, queued: 0, recent: 0 },
  });
  const [open, setOpen] = useState(false);
  const pollRef = useRef();

  useEffect(() => {
    let stopped = false;

    async function poll() {
      try {
        const { data } = await axios.get(`${API}/activity`, { timeout: 8000 });
        if (!stopped && data) setActivity(data);
      } catch (_e) { /* offline / auth timeout — retry next tick */ }
    }

    poll();
    // Faster poll when the drawer is open so status updates feel live.
    const interval = open ? 2000 : 5000;
    pollRef.current = setInterval(poll, interval);
    return () => {
      stopped = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open]);

  const totalActive = activity.counts.running + activity.counts.queued;
  const hasAnyActivity = totalActive > 0 || activity.counts.recent > 0;

  // Hide the pill entirely when there's nothing to show — keeps the
  // top bar clean during quiet periods.
  if (!hasAnyActivity && !open) return null;

  const pillColor = totalActive > 0
    ? "bg-primary/10 text-primary border-primary/30"
    : "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1.5 h-9 px-3 rounded-full border transition-colors font-semibold text-[11px] ${pillColor}`}
        data-testid="activity-pill"
        title={totalActive > 0
          ? `${activity.counts.running} running · ${activity.counts.queued} queued`
          : "No active work — recent runs available"}
      >
        {activity.counts.running > 0
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Activity className="h-3.5 w-3.5" />}
        {totalActive > 0 ? (
          <span data-testid="activity-pill-count" className="tabular-nums">
            {activity.counts.running > 0 && `${activity.counts.running} running`}
            {activity.counts.running > 0 && activity.counts.queued > 0 && " · "}
            {activity.counts.queued > 0 && `${activity.counts.queued} queued`}
          </span>
        ) : (
          <span>Recent activity</span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen} modal={false}>
        <SheetContent
          side="right"
          className="w-[420px] sm:max-w-[420px] flex flex-col p-0 gap-0"
          data-testid="activity-sheet"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Activity className="h-4 w-4 text-primary" />
              Analyzer activity
            </SheetTitle>
            <SheetDescription className="text-xs">
              Every running / queued / recently-finished analysis across
              your jobs. Click a row to jump to that job.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            <SessionSection
              title="Running now"
              tone="running"
              rows={activity.running}
              emptyLabel="Nothing running right now."
              onOpen={(s) => { setOpen(false); nav(`/session/${s.id}`); }}
            />
            {/* Queued rows navigate to the parent Job page (not the
                session view) because a queued session has nothing to
                show yet — the file list + progress bars only appear
                once the semaphore slot frees and status flips to
                downloading. The Job page's Recent Analyses card is
                the right landing surface for "watch until it starts". */}
            <SessionSection
              title="Queued"
              tone="queued"
              rows={activity.queued}
              emptyLabel="No sessions waiting for a free slot."
              onOpen={(s) => { setOpen(false); nav(`/jobs/${s.job_id}`); }}
            />
            <SessionSection
              title="Finished in the last 10 min"
              tone="recent"
              rows={activity.recent}
              emptyLabel="Nothing has finished recently."
              onOpen={(s) => { setOpen(false); nav(`/session/${s.id}`); }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function SessionSection({ title, tone, rows, emptyLabel, onOpen }) {
  const dotColour = {
    running: "bg-primary animate-pulse",
    queued: "bg-slate-400",
    recent: "bg-emerald-500",
  }[tone] || "bg-slate-300";

  return (
    <div data-testid={`activity-section-${tone}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2 flex items-center gap-2">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColour}`} />
        {title}
        <span className="ml-auto text-slate-400 tabular-nums font-mono">
          {rows.length}
        </span>
      </p>
      {rows.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic pb-1" data-testid={`activity-empty-${tone}`}>
          {emptyLabel}
        </p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((s) => (
            <SessionRow key={s.id} session={s} tone={tone} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

function SessionRow({ session, tone, onOpen }) {
  const total = session.download_total ?? session.photo_count ?? 0;
  const done = session.analyzed_count ?? session.downloaded_count ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null;

  // Prefer the AI-grouping phase text when a grouping run is in flight
  // for this session — it's more informative than "downloading N/M".
  const gp = session.ai_grouping_progress;
  const gpActive = gp && gp.status === "running";
  const phaseText = gpActive
    ? gp.phase
    : session.status === "queued"
      ? "Queued — waiting for a free slot"
      : session.status === "downloading"
        ? `Downloading ${done}/${total}`
        : session.status === "analyzing"
          ? `Analyzing ${done}/${total}`
          : session.status === "complete" || session.status === "ready"
            ? `Complete — ${done}/${total}`
            : session.status === "failed"
              ? (session.error || "Failed")
              : session.status;

  const Icon = session.status === "failed"
    ? XCircle
    : session.status === "complete" || session.status === "ready"
      ? CheckCircle2
      : session.status === "queued"
        ? Clock
        : Layers;

  return (
    <button
      type="button"
      onClick={() => onOpen(session)}
      className="w-full text-left rounded-md border border-border bg-white hover:bg-slate-50 hover:border-primary/40 transition-colors p-2.5 flex items-start gap-2.5"
      data-testid={`activity-row-${session.id}`}
    >
      <Icon
        className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${
          tone === "running" ? "text-primary"
            : tone === "queued" ? "text-slate-400"
              : session.status === "failed" ? "text-red-500"
                : "text-emerald-500"
        }`}
      />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-slate-900 truncate">
          {session.job_name || "Untitled job"}
          {session.subfolder && (
            <span className="text-slate-400 font-normal">
              {" · "}{session.subfolder}
            </span>
          )}
        </p>
        <p className="text-[11px] text-slate-600 truncate mt-0.5">
          {phaseText}
          {session.batch_label && (
            <span className="text-primary font-mono ml-1.5">
              {session.batch_label}
            </span>
          )}
        </p>
        {pct !== null && tone === "running" && (
          <div className="mt-1.5 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>
      <ChevronRight className="h-3.5 w-3.5 text-slate-300 mt-1 shrink-0" />
    </button>
  );
}
